/**
 * Wishlist "Coming Soon" Guard
 *
 * Disables the Add-to-Cart button on wishlist product cards
 * when the product has a `custom.release_date` metafield set.
 *
 * Only runs on the /apps/wishlist page. Uses the Section Rendering
 * API with `coming-soon-check` section to read the metafield value.
 *
 * Loaded with defer from theme.liquid. Expects a global
 * `window.__comingSoonLabel` string set by Liquid before this script.
 */
(function () {
  'use strict';

  const SECTION_ID = 'coming-soon-check';
  const LABEL      = window.__comingSoonLabel || 'Coming Soon';

  /* ------------------------------------------------------------------ */
  /*  Bootstrap — wait for the wishlist app to render <wishlist-page>    */
  /* ------------------------------------------------------------------ */
  function waitForWishlist() {
    const root = document.querySelector('wishlist-page');
    if (root) {
      observe(root);
      return;
    }

    new MutationObserver((mutations, observer) => {
      const root = document.querySelector('wishlist-page');
      if (root) {
        observer.disconnect();
        observe(root);
      }
    }).observe(document.body, { childList: true, subtree: true });
  }

  /* ------------------------------------------------------------------ */
  /*  Observe — watch for cards added / updated by the wishlist app      */
  /* ------------------------------------------------------------------ */
  function observe(root) {
    scan(root);
    new MutationObserver(() => scan(root))
      .observe(root, { childList: true, subtree: true });
  }

  /* ------------------------------------------------------------------ */
  /*  Scan — iterate every <wishlist-product-card> that hasn't been      */
  /*         checked yet (skip cards whose content isn't rendered yet)   */
  /* ------------------------------------------------------------------ */
  function scan(root) {
    root.querySelectorAll('wishlist-product-card').forEach((card) => {
      if (card.dataset.comingSoonChecked) return;

      const link = card.querySelector('a.wk-image-link, a.wk-text-link');
      if (!link) return;                       // content not rendered yet — retry on next mutation

      card.dataset.comingSoonChecked = 'true';  // mark only after we confirm content exists

      const match = link.getAttribute('href').match(/\/products\/([^?#/]+)/);
      if (!match) return;

      fetchRelease(match[1], card);
    });
  }

  /* ------------------------------------------------------------------ */
  /*  Fetch — call the Section Rendering API to read the metafield      */
  /* ------------------------------------------------------------------ */
  function fetchRelease(handle, card) {
    fetch('/products/' + handle + '?section_id=' + SECTION_ID)
      .then((res) => {
        if (!res.ok) return null;
        return res.text();
      })
      .then((html) => {
        if (!html) return;

        const doc  = new DOMParser().parseFromString(html, 'text/html');
        const meta = doc.querySelector('[data-release-date]');
        if (!meta) return;

        const date      = meta.getAttribute('data-release-date');
        const formId    = meta.getAttribute('data-coming-soon-form-id');
        const formIdCom = meta.getAttribute('data-coming-soon-form-id-com');

        const hasActiveReleaseDate = date && date.trim() !== '' && new Date(date) > new Date();
        const hasComingSoonFormId  = (formId && formId.trim() !== '') || (formIdCom && formIdCom.trim() !== '');

        if (hasActiveReleaseDate || hasComingSoonFormId) {
          disableCard(card);
        }
      })
      .catch(() => { /* silently skip on network errors */ });
  }

  /* ------------------------------------------------------------------ */
  /*  Disable — make the card inert so the product cannot be purchased  */
  /* ------------------------------------------------------------------ */
  function disableCard(card) {
    card.dataset.comingSoonLocked = 'true';

    const btn = card.querySelector('.wk-cta-button');
    if (btn) {
      btn.disabled = true;
      btn.setAttribute('type', 'button');
      btn.removeAttribute('formaction');
      btn.style.opacity    = '0.6';
      btn.style.cursor     = 'not-allowed';

      const label = btn.querySelector('.wk-cta-label');
      if (label) label.textContent = LABEL;

      btn.addEventListener('click', blockClick, true);
    }

    // Variant selects and quantity inputs are left fully interactive.
    // The WK app persists variant selections (size, material, etc.) when
    // the user interacts with the card, so these controls must remain
    // clickable. Only the CTA button and form submission are blocked.

    card.addEventListener('submit', blockClick, true);
  }

  function blockClick(e) {
    e.preventDefault();
    e.stopImmediatePropagation();
    return false;
  }

  /* ------------------------------------------------------------------ */
  /*  Init                                                              */
  /* ------------------------------------------------------------------ */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', waitForWishlist);
  } else {
    waitForWishlist();
  }
})();
